import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { ResponseObject } from 'src/app/models/response-object.model';
import { WithholdingService } from 'src/app/services/withholding.service';

export interface WithholdingResponse {
  id: number;
  company: number;
  creationDate: number | Date;
  vendorId: number;
  vendorName: string;
  vendorNit: string;
  totalRetention: number;
  issued: boolean;
  constancy: string;
  date: number | Date;
  type: string;
  annulment: boolean;
}

@Component({
  selector: 'app-history-withholdings',
  templateUrl: './history-withholdings.component.html',
  styleUrls: ['./history-withholdings.component.scss']
})
export class HistoryWithholdingsComponent implements OnInit {

  withholdings: WithholdingResponse[] = [];

  pagination = {
    total: [],
    filter: [],
    pageSize: 5,
    startElement: 0,
    lastElement: 0,
    pageIndex: 0
  };

  search = '';
  typeFilter = '';
  issuedFilter = '';
  constancyFilter = '';

  startDate = '';
  endDate = '';

  loading = false;
  
  waitingFor: WithholdingResponse | null = null;
  annulling: { [key: number]: boolean } = {};

  constructor(
    private withholdingService: WithholdingService,
    private toastService: ToastrService
  ) {
    const today = new Date();

    this.endDate = today.toISOString().split('T')[0];

    today.setDate(1);
    this.startDate = today.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    this.getWithholdings();
  }

  getWithholdings(): void {
    if (!this.startDate || !this.endDate) {
      return;
    }

    this.loading = true;

    this.withholdingService.getWithholdings(this.startDate, this.endDate).subscribe({
      next: (response: ResponseObject) => {
        this.withholdings = response.data || [];

        this.withholdings = this.withholdings.sort((a, b) => {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();

          if (dateB !== dateA) {
            return dateB - dateA;
          }

          const creationA = new Date(a.creationDate).getTime();
          const creationB = new Date(b.creationDate).getTime();

          return creationB - creationA;
        });

        this.pagination.total = this.withholdings;
        this.resetPagination();
        this.searching();

        this.loading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.loading = false;
        this.toastService.error(error.error?.message || 'Error al obtener las retenciones');
      }
    });
  }

  checkDateValue(): void {
    if (this.startDate && this.endDate) {
      this.getWithholdings();
    }
  }

  searching(): void {
    this.pagination.total = this.withholdings.filter((withholding) => {
      return this.matchSearch(withholding)
        && this.matchType(withholding)
        && this.matchIssued(withholding)
        && this.matchConstancy(withholding);
    });

    this.pagination.pageIndex = 0;
    this.pagination.startElement = 0;
    this.pagination.filter = this.pagination.total.slice(0, this.pagination.pageSize);
    this.pagination.lastElement = this.pagination.filter.length;
  }

  matchSearch(withholding: WithholdingResponse): boolean {
    if (!this.search) {
      return true;
    }

    const value = this.search.toLowerCase().trim();

    return (withholding.vendorName || '').toLowerCase().includes(value)
      || (withholding.vendorNit || '').toLowerCase().includes(value)
      || (withholding.constancy || '').toLowerCase().includes(value)
      || (withholding.type || '').toLowerCase().includes(value)
      || String(withholding.totalRetention || '').includes(value);
  }

  matchType(withholding: WithholdingResponse): boolean {
    if (!this.typeFilter) {
      return true;
    }

    return (withholding.type || '').toLowerCase() === this.typeFilter.toLowerCase();
  }

  matchIssued(withholding: WithholdingResponse): boolean {
    if (this.issuedFilter === '') {
      return true;
    }

    return withholding.issued === (this.issuedFilter === 'true');
  }

  matchConstancy(withholding: WithholdingResponse): boolean {
    if (!this.constancyFilter) {
      return true;
    }

    return (withholding.constancy || '')
      .toLowerCase()
      .includes(this.constancyFilter.toLowerCase().trim());
  }

  resetPagination(): void {
    this.pagination.filter = this.pagination.total.slice(0, this.pagination.pageSize);
    this.pagination.pageIndex = 0;
    this.pagination.lastElement = this.pagination.filter.length;
    this.pagination.startElement = 0;
  }

  cleanFilters(): void {
    this.search = '';
    this.typeFilter = '';
    this.issuedFilter = '';
    this.constancyFilter = '';
    this.searching();
  }

  
  showAnnulmentWarning(withholding: WithholdingResponse): void {
    this.waitingFor = withholding;
  }

  resetAnnulmentDiv(): void {
    this.waitingFor = null;
  }

  confirmAnnulment(withholding: WithholdingResponse): void {
    this.annulling[withholding.id] = true;

    this.withholdingService.annulmentWithholding(withholding.id).subscribe({
      next: () => {
        this.toastService.success('Retención anulada correctamente');

        withholding.annulment = true;
        this.waitingFor = null;
        this.annulling[withholding.id] = false;

        this.searching();
      },
      error: (error: HttpErrorResponse) => {
        this.annulling[withholding.id] = false;
        this.toastService.error(error.error?.message || 'Error al anular la retención');
      }
    });
  }
}